import React from "react";
import { Alert, Typography } from "@arco-design/web-react";
import { analyze, MIN_CONVERSIONS_TOTAL, MIN_VIEWS_PER_VARIANT } from "../abStats";

const { Text } = Typography;

// Summarizes the campaign's form A/B test: each variant's conversion rate with
// its 95% range, and a plain-language verdict (winner / keep running / need
// more data). Views and conversions are per visitor, so rate = conversions/views.

const pct = (x) => (x * 100).toFixed(x > 0 && x < 0.1 ? 1 : 0) + "%";
const num = (n) => Number(n || 0).toLocaleString();

function verdict(result) {
    switch (result.status) {
        case "winner":
            return {
                type: "success",
                title: `"${result.winner.name}" is winning`,
                content: `It converts at ${pct(result.winner.rate)} vs ${pct(result.runnerUp.rate)} for "${result.runnerUp.name}"` +
                    (result.lift != null ? ` — ${Math.round(result.lift * 100)}% better` : "") +
                    `, with 95% confidence. You can pause the others and send everyone to the winner.`,
            };
        case "no_difference":
            return {
                type: "info",
                title: "No clear winner yet",
                content: `"${result.leader.name}" leads (${pct(result.leader.rate)} vs ${pct(result.runnerUp.rate)}), but the gap could still be chance.` +
                    (result.moreViews != null
                        ? ` If the gap holds, about ${num(result.moreViews)} more views per form should settle it.`
                        : " The rates are identical so far."),
            };
        case "collecting": {
            const need = [];
            if (result.viewsToGo > 0) need.push(`${num(result.viewsToGo)} more views on the least-seen form`);
            if (result.conversionsToGo > 0) need.push(`${num(result.conversionsToGo)} more signups`);
            return {
                type: "warning",
                title: "Collecting data",
                content: `Too early to call — needs ${need.join(" and ")} (at least ${MIN_VIEWS_PER_VARIANT} views per form and ${MIN_CONVERSIONS_TOTAL} signups in total).`,
            };
        }
        default:
            return null;
    }
}

export default function ABReadout({ formVariants }) {
    const result = analyze(formVariants);
    if (result.rows.length === 0) return null;
    if (result.status === "single") {
        return (
            <Alert
                style={{ marginBottom: 12 }}
                type="info"
                content={result.rows.length === 1
                    ? "To A/B test, clone this form, change one thing (the headline, image or button text) and set both to Active."
                    : "Only one form is active. Set two or more to Active to compare them."}
            />
        );
    }

    const v = verdict(result);
    const maxHigh = Math.max(0.01, ...result.rows.map((r) => r.ci.high));
    const leaderId = (result.winner || result.leader || {}).id;

    return (
        <div style={{ marginBottom: 16 }}>
            <Alert type={v.type} title={v.title} content={v.content} style={{ marginBottom: 10 }} />
            <div style={{ border: "1px solid var(--color-border-2)", borderRadius: 6, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "var(--color-fill-2)", color: "var(--color-text-2)", textAlign: "left" }}>
                            <th style={cell}>Form</th>
                            <th style={{ ...cell, textAlign: "right" }}>Views</th>
                            <th style={{ ...cell, textAlign: "right" }}>Signups</th>
                            <th style={{ ...cell, textAlign: "right" }}>Rate</th>
                            <th style={{ ...cell, width: "34%" }}>95% range</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.rows.map((r) => (
                            <tr key={r.id} style={{ borderTop: "1px solid var(--color-border-1)", opacity: r.active ? 1 : 0.55 }}>
                                <td style={cell}>
                                    <Text style={{ fontWeight: r.id === leaderId ? 600 : 400 }}>{r.name}</Text>
                                    {!r.active && <Text type="secondary" style={{ fontSize: 12 }}> (paused)</Text>}
                                </td>
                                <td style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(r.views)}</td>
                                <td style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(r.conversions)}</td>
                                <td style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{r.views ? pct(r.rate) : "—"}</td>
                                <td style={cell}>
                                    {r.views > 0 && (
                                        <div title={`${pct(r.ci.low)} – ${pct(r.ci.high)}`} style={{ position: "relative", height: 10, background: "var(--color-fill-2)", borderRadius: 5 }}>
                                            <div style={{
                                                position: "absolute", top: 0, bottom: 0, borderRadius: 5,
                                                left: `${(r.ci.low / maxHigh) * 100}%`,
                                                width: `${Math.max(1, ((r.ci.high - r.ci.low) / maxHigh) * 100)}%`,
                                                background: r.id === leaderId && result.status === "winner" ? "rgb(var(--green-5))" : "rgb(var(--arcoblue-4))",
                                            }} />
                                            <div style={{
                                                position: "absolute", top: -2, bottom: -2, width: 2, borderRadius: 1,
                                                left: `calc(${(r.rate / maxHigh) * 100}% - 1px)`,
                                                background: "var(--color-text-1)",
                                            }} />
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                Views and signups count each visitor once. Bars show where each form's true rate likely sits; overlapping bars mean it's too close to call.
            </Text>
        </div>
    );
}

const cell = { padding: "8px 10px" };
